import { useMemo, useEffect } from 'react';
import { useFormikContext } from 'formik';
import TextInput from '@adsk/alloy-react-text-input';
import { useUser } from '@adsk/alloy-provider';
import { T } from '../../helpers/i18n';
import { AssigneePicker, CommonFormField } from '../../components';
import { useRfiForm, useRfiFormField } from '../RfiFormProvider';
import { ISSUE_ATTRIBUTE_NAMES } from '../../constants/issue-attribute-names';
import { EMPTY_ARRAY } from '../../constants';
import { Assignee, RfiStatus } from '../../sdk';
import { RfiCreateFormValues } from '../types';

const FIELD_NAME = ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO;

const AssignedTo = () => {
  const formik = useFormikContext<RfiCreateFormValues>();
  const { selectedTransition } = useRfiForm();
  const { isFieldsMandatory, errorMessage, isSubmitting, fieldRef } = useRfiFormField(FIELD_NAME);

  const { user: currentUser } = useUser();
  const userDisplayName = currentUser?.name || '';
  const assignedToOptions = useMemo(
    () => Object.values(selectedTransition?.assigneeUsers || EMPTY_ARRAY),
    [selectedTransition],
  );

  const handleSelectAssignee = (assignees: Assignee[]) => {
    formik.setFieldValue(FIELD_NAME, assignees);
  };

  const selectedAssignees = formik.values[FIELD_NAME];

  // If the selected assignees are not in the list of available assignees, remove them
  useEffect(() => {
    const validAssignees = selectedAssignees.filter(
      (assignee) => assignedToOptions.findIndex((option) => option.id === assignee.id) > -1,
    );

    if (validAssignees.length !== selectedAssignees.length) {
      handleSelectAssignee(validAssignees);
    }
  }, [selectedAssignees, assignedToOptions]);

  return (
    <CommonFormField
      label={T.translate('new.assignedTo')}
      required={isFieldsMandatory}
      error={isFieldsMandatory && errorMessage}
      ref={fieldRef}
    >
      {formik.values.status === RfiStatus.Draft ? (
        <TextInput
          name={FIELD_NAME}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={userDisplayName}
          error={!!errorMessage}
          disabled
        />
      ) : (
        <AssigneePicker
          onChange={handleSelectAssignee}
          selectedAssignees={selectedAssignees}
          assigneesOptions={assignedToOptions}
          clearable
          disabled={isSubmitting}
          maxAssignees={selectedTransition?.maxAssignees}
          error={!!errorMessage}
        />
      )}
    </CommonFormField>
  );
};
export default AssignedTo;
